import React, { useState, useCallback, useEffect, useContext } from 'react';
import {
    MDBSideNav,
    MDBSideNavMenu,
    MDBSideNavItem,
    MDBSideNavLink,
    MDBSideNavCollapse,
    MDBIcon,
    MDBContainer,
    MDBNavbar,
    MDBInput,
    MDBInputGroup,
    MDBNavbarToggler,
    MDBNavbarNav,
    MDBNavbarItem,
    MDBDropdown,
    MDBDropdownToggle,
    MDBDropdownMenu,
    MDBDropdownItem,
    MDBBadge,
    MDBCollapse,
    MDBSwitch,
} from 'mdb-react-ui-kit';
import { NavLink, useLocation } from "react-router-dom";
import { signOut } from 'aws-amplify/auth';
import { useTranslation } from "react-i18next";
import LangSelect from "./components-user/LangSelect";
import { DevContext } from "./App";
import ThemeToggle from "./utils/ThemeToggle";


/** className builder for child NavLinks inside accordion sections */
const childLinkClass = ({ isActive }) =>
    `ripple ripple-surface sidenav-link${isActive ? " sidenav-child-active" : ""}`;

/** className builder for top-level NavLinks (not inside an accordion) */
const topLinkClass = ({ isActive }) =>
    `ripple ripple-surface sidenav-link${isActive ? " active" : ""}`;

export default function Navigation(props) {
    const { t } = useTranslation();
    const { isDevMode, setIsDevMode, isKioskMode, setIsKioskMode } = useContext(DevContext);
    const location = useLocation();

    const [collapseOpened, setCollapseOpened] = useState('accordionCollapse1');
    const [basicOpen, setBasicOpen] = useState(true);
    const [mode, setMode] = useState('side');

    const handleSetOpen = (value) => {
        setBasicOpen(value);
        props.setDesktopFullscreen(!value);
    };

    const toggleAccordion = (value) => {
        value !== collapseOpened ? setCollapseOpened(value) : setCollapseOpened('');
    };

    const handleResize = useCallback(() => {
        if (!window.matchMedia('screen and (min-width: 992px)').matches) {
            setMode('side');
            return setBasicOpen(false);
        }
        setMode('push');
        return setBasicOpen(true);
    }, []);

    useEffect(() => {
        handleResize();
    }, [handleResize]);

    useEffect(() => {
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);


    return (
        <>
            <header className={isKioskMode ? "d-none" : ""}>
                <MDBSideNav backdrop={false} mode={mode} open={basicOpen} getOpenState={(e) => setBasicOpen(e)} bgColor='primary'>
                    <div className='d-flex justify-content-center py-4'>
                        <img
                            id='MDB-logo'
                            src='/logo_neg_cp.svg'
                            alt='Cleanroom Processes'
                            draggable='false'
                            height={"65px"}
                        />
                    </div>
                    <h5 className="text-center">{t("Management Console")}</h5>
                    <MDBSideNavMenu>
                        <MDBSideNavItem>
                            <MDBSideNavLink>
                                <MDBIcon fas icon="home me-3" />

                                {t("Dashboard")}
                            </MDBSideNavLink>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <hr className="mx-3 my-0"></hr>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <MDBSideNavLink
                                icon='angle-down'
                                shouldBeExpanded={collapseOpened === 'accordionCollapse1'}
                                onClick={() => toggleAccordion('accordionCollapse1')}
                                className={["/admin/users", "/admin/cognito-users"].some(p => location.pathname.startsWith(p)) ? "sidenav-parent-active" : ""}
                            >
                                <MDBIcon icon='users' className='fas me-3' />
                                {t("Users & Visitors")}
                            </MDBSideNavLink>
                            <MDBSideNavCollapse id='accordionCollapse1' open={collapseOpened === 'accordionCollapse1'}>
                                <NavLink to="/admin/users" className={childLinkClass}>{t("Platform Users")}</NavLink>
                                <NavLink to="/admin/cognito-users" className={childLinkClass}>{t("Cognito Users")}</NavLink>
                                <MDBSideNavLink href="/admin/users">{t("Visitors")}</MDBSideNavLink>
                                <MDBSideNavLink>{t("Admins & Moderators")}</MDBSideNavLink>
                                <MDBSideNavLink>{t("Deletion Requests")}</MDBSideNavLink>
                            </MDBSideNavCollapse>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <NavLink to="/admin/companies" className={topLinkClass}>
                                <MDBIcon icon='building' className='fas me-3' />
                                {t("Companys")}
                            </NavLink>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <MDBSideNavLink
                                icon='angle-down'
                                shouldBeExpanded={collapseOpened === 'accordionCollapseCompanyData'}
                                onClick={() => toggleAccordion('accordionCollapseCompanyData')}
                                className={["/admin/company-products", "/admin/news", "/admin/company-jobs"].some(p => location.pathname.startsWith(p)) ? "sidenav-parent-active" : ""}
                            >
                                <MDBIcon icon='database' className='fas me-3' />
                                {t("Company Data")}
                            </MDBSideNavLink>
                            <MDBSideNavCollapse id='accordionCollapseCompanyData' open={collapseOpened === 'accordionCollapseCompanyData'}>
                                <NavLink to="/admin/company-products" className={childLinkClass}>{t("Products")}</NavLink>
                                <NavLink to="/admin/news" className={childLinkClass}>{t("News")}</NavLink>
                                <NavLink to="/admin/company-jobs" className={childLinkClass}>{t("Jobs")}</NavLink>
                            </MDBSideNavCollapse>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <MDBSideNavLink
                                icon='angle-down'
                                shouldBeExpanded={collapseOpened === 'accordionCollapseMediathek'}
                                onClick={() => toggleAccordion('accordionCollapseMediathek')}
                                className={location.pathname.startsWith("/admin/mediathek") ? "sidenav-parent-active" : ""}
                            >
                                <MDBIcon icon='photo-video' className='fas me-3' />
                                {t("Media library")}
                            </MDBSideNavLink>
                            <MDBSideNavCollapse id='accordionCollapseMediathek' open={collapseOpened === 'accordionCollapseMediathek'}>
                                <NavLink to="/admin/mediathek" end className={childLinkClass}>{t("Series & formats")}</NavLink>
                                <NavLink to="/admin/mediathek/items" className={childLinkClass}>{t("Items")}</NavLink>
                            </MDBSideNavCollapse>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <NavLink to="/admin/industries" className={topLinkClass}>
                                <MDBIcon icon='city' className='fas me-3' />
                                {t("Industries")}
                            </NavLink>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <MDBSideNavLink
                                icon='angle-down'
                                shouldBeExpanded={collapseOpened === 'accordionCollapse2'}
                                onClick={() => toggleAccordion('accordionCollapse2')}
                                className={["/admin/events", "/admin/booked-booths", "/admin/custom-booth-requests", "/admin/event/invitation-codes", "/admin/lectures", "/admin/booked-equipment", "/admin/event-carpets"].some(p => location.pathname.startsWith(p)) ? "sidenav-parent-active" : ""}
                            >
                                <MDBIcon icon='calendar-day' className='fas me-3' />
                                {t("Events")}
                            </MDBSideNavLink>
                            <MDBSideNavCollapse id='accordionCollapse2' open={collapseOpened === 'accordionCollapse2'}>
                                <NavLink to="/admin/events" className={childLinkClass}>{t("Events")}</NavLink>
                                <NavLink to="/admin/booked-booths" className={childLinkClass}>{t("Booked Booths")}</NavLink>
                                <NavLink to="/admin/custom-booth-requests" className={childLinkClass}>{t("Individual Requests")}</NavLink>
                                <NavLink to="/admin/event/invitation-codes" className={childLinkClass}>{t("Invitation Codes")}</NavLink>
                                <MDBSideNavLink href='/admin/lectures'>{t("Lectures")}</MDBSideNavLink>
                                <NavLink to="/admin/booked-equipment" className={childLinkClass}>{t("Furnishing & Equipment")}</NavLink>
                                <NavLink to="/admin/event-carpets" className={childLinkClass}>{t("Carpets")}</NavLink>
                            </MDBSideNavCollapse>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <MDBSideNavLink
                                icon='angle-down'
                                shouldBeExpanded={collapseOpened === 'accordionCollapseinsight'}
                                onClick={() => toggleAccordion('accordionCollapseinsight')}
                                className={["/admin/insight/courses", "/admin/academy/seminars", "/admin/video-progress"].some(p => location.pathname.startsWith(p)) ? "sidenav-parent-active" : ""}
                            >
                                <MDBIcon icon='graduation-cap' className='fas me-3' />
                                Academy
                            </MDBSideNavLink>
                            <MDBSideNavCollapse id='accordionCollapseinsight' open={collapseOpened === 'accordionCollapseinsight'}>
                                <NavLink to="/admin/insight/courses" className={childLinkClass}>{t("Basic Courses")}</NavLink>
                                <NavLink to="/admin/academy/seminars" className={childLinkClass}>{t("Seminars")}</NavLink>
                                <NavLink to="/admin/video-progress" className={childLinkClass}>{t("Video Views")}</NavLink>
                            </MDBSideNavCollapse>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <MDBSideNavLink
                                icon='angle-down'
                                shouldBeExpanded={collapseOpened === 'accordionCollapseProducts'}
                                onClick={() => toggleAccordion('accordionCollapseProducts')}
                                className={["/admin/products", "/admin/stockreservations", "/admin/booths", "/admin/booth/", "/admin/3d"].some(p => location.pathname.startsWith(p)) ? "sidenav-parent-active" : ""}
                            >
                                <MDBIcon icon='box' className='fas me-3' />
                                {t("Products")}
                            </MDBSideNavLink>
                            <MDBSideNavCollapse id='accordionCollapseProducts' open={collapseOpened === 'accordionCollapseProducts'}>
                                <NavLink to="/admin/products" className={childLinkClass}>
                                    {t("All Products")}
                                </NavLink>
                                <NavLink to="/admin/stockreservations" className={childLinkClass}>
                                    {t("Stock Reservations")}
                                </NavLink>
                                <NavLink to="/admin/booths" className={childLinkClass}>
                                    {t("Booths")}
                                </NavLink>
                            </MDBSideNavCollapse>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <NavLink to="/admin/discounts" className={topLinkClass}>
                                <MDBIcon icon='percent' className='fas me-3' />
                                {t("Discounts")}
                            </NavLink>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <MDBSideNavLink
                                icon='angle-down'
                                shouldBeExpanded={collapseOpened === 'accordionCollapse3'}
                                onClick={() => toggleAccordion('accordionCollapse3')}
                            >
                                <MDBIcon icon='file-invoice' className='fas me-3' />
                                {t("Accounting")}
                            </MDBSideNavLink>
                            <MDBSideNavCollapse id='accordionCollapse3' open={collapseOpened === 'accordionCollapse3'}>
                                <MDBSideNavLink>{t("All Invoices")}</MDBSideNavLink>
                                <MDBSideNavLink>{t("Invoice Settings")}</MDBSideNavLink>
                            </MDBSideNavCollapse>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <NavLink
                                to="/admin/orders"
                                className={topLinkClass}
                            >
                                <MDBIcon icon="shopping-bag" className="fas me-3" />
                                {t("Buchungen")}
                            </NavLink>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <NavLink
                                to="/admin/tickets"
                                className={topLinkClass}
                            >
                                <MDBIcon icon="ticket" className="fas me-3" />
                                {t("Tickets")}
                            </NavLink>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <NavLink to="/admin/lecture_scheduling" className={topLinkClass}>
                                <MDBIcon icon='building' className='fas me-3' />
                                Event Kalendar
                            </NavLink>
                        </MDBSideNavItem>

                        <MDBSideNavItem>
                            <NavLink to="/admin/lectures" className={topLinkClass}>
                                <MDBIcon icon='building' className='fas me-3' />
                                {t("Lectures")}
                            </NavLink>
                        </MDBSideNavItem>

                        <MDBSideNavItem>
                            <NavLink to="/companies-attending-event" className={topLinkClass}>
                                <MDBIcon icon='building' className='fas me-3' />
                                Unternehmen LNG2024
                            </NavLink>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <NavLink to="/admin/printticket" className={topLinkClass}>
                                <MDBIcon icon='ticket' className='fas me-3' />
                                Print Tickets
                            </NavLink>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <NavLink to="/admin/chatbot" className={topLinkClass}>
                                <MDBIcon icon='robot' className='fas me-3' />
                                AI Chatbot
                            </NavLink>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <hr className="mx-3 my-0"></hr>
                        </MDBSideNavItem>
                        <MDBSideNavItem>
                            <MDBSideNavLink
                                icon='angle-down'
                                shouldBeExpanded={collapseOpened === 'accordionCollapseSystem'}
                                onClick={() => toggleAccordion('accordionCollapseSystem')}
                                className={location.pathname.startsWith("/admin/system") ? "sidenav-parent-active" : ""}
                            >
                                <MDBIcon icon='cog' className='fas me-3' />
                                {t("System Settings")}
                            </MDBSideNavLink>
                            <MDBSideNavCollapse id='accordionCollapseSystem' open={collapseOpened === 'accordionCollapseSystem'}>
                                <NavLink to="/admin/system/document-settings" className={childLinkClass}>{t("Document Settings")}</NavLink>
                            </MDBSideNavCollapse>
                        </MDBSideNavItem>

                    </MDBSideNavMenu>
                    <div className="d-inline d-md-none fixed-bottom">
                        <LangSelect />

                    </div>
                    <MDBIcon
                        fas
                        // className="d-lg-none"
                        icon="angle-double-left"
                        onClick={() => { handleSetOpen(!basicOpen) }}
                        style={{
                            position: "fixed",
                            top: "1rem",
                            right: "1rem"
                        }}
                    />
                    <>
                        <div
                            className={"d-lg-none " + (basicOpen ? "sidenav-overlay" : "sidenav-overlay d-none")}
                            style={{
                                width: "100vw",
                                height: "100vh",
                                position: "fixed",
                                top: "0",
                                left: "240px",
                            }}
                            onClick={() => { handleSetOpen(!basicOpen) }}>
                        </div>
                    </>



                </MDBSideNav>


                <MDBNavbar fixed='top' expand='lg' id='main-navbar' bgColor='secondary'>
                    <MDBContainer fluid>
                        <MDBNavbarToggler
                            className="d-flex"
                            aria-controls='navbarSupportedContent'
                            aria-expanded='false'
                            aria-label='Toggle navigation'
                            onClick={() => handleSetOpen(!basicOpen)}
                        >
                            <MDBIcon icon='bars' fas />
                        </MDBNavbarToggler>

                        <MDBInputGroup className='d-flex justify-content-center my-auto mainSearch'>
                            <MDBInput className="bg-light" wrapperClass="mainSearch_InputWrapper" label={t("Search")} />
                        </MDBInputGroup>

                        <MDBCollapse navbar open={true} className="d-flex ms-2 ms-md-0" style={{ maxHeight: "50px" }}>
                            <MDBNavbarNav right fullWidth={false}>
                                {/* <MDBNavbarItem>
                <MDBDropdown>
                <MDBDropdownToggle tag='a' className='nav-link text-dark'>
                    <MDBIcon icon='bell' />
                    <MDBBadge notification color='danger' pill>
                    1
                    </MDBBadge>
                </MDBDropdownToggle>
                <MDBDropdownMenu>
                    <MDBDropdownItem link>Action
                    </MDBDropdownItem>
                    <MDBDropdownItem link>Another action
                    </MDBDropdownItem>
                    <MDBDropdownItem link>Something else here
                    </MDBDropdownItem>
                </MDBDropdownMenu>
                </MDBDropdown>
            </MDBNavbarItem> */}
                                <MDBNavbarItem className="d-none d-md-inline">
                                    <LangSelect />
                                </MDBNavbarItem>
                                <MDBNavbarItem>
                                    <ThemeToggle />
                                </MDBNavbarItem>

                                <MDBNavbarItem>
                                    <MDBDropdown>
                                        <MDBDropdownToggle tag='a' className='nav-link'>
                                            Admin
                                        </MDBDropdownToggle>
                                        <MDBDropdownMenu>
                                            <MDBDropdownItem link onClick={() => props.setAdminAsUser(true)}>{t("Switch to User")}</MDBDropdownItem>
                                            <MDBDropdownItem link>{t("My profile")}
                                            </MDBDropdownItem>
                                            <MDBDropdownItem link>{t("Settings")}
                                            </MDBDropdownItem>
                                            <MDBDropdownItem link onClick={() => signOut()}>{t("Logout")}
                                            </MDBDropdownItem>
                                            <MDBDropdownItem link>
                                                <span className="fw-bold">{t("Dev Mode")}</span>
                                                <MDBSwitch
                                                    className="mt-2"
                                                    checked={isDevMode}
                                                    onChange={() => setIsDevMode(!isDevMode)}
                                                />
                                            </MDBDropdownItem>
                                        </MDBDropdownMenu>
                                    </MDBDropdown>
                                </MDBNavbarItem>
                            </MDBNavbarNav>
                        </MDBCollapse>
                    </MDBContainer>
                </MDBNavbar>
            </header>
        </>
    );
}